import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TapChips, ChipSection, yearSpans } from "@/components/search/TapChips";
import { statusLabel } from "@/lib/status";
import { titleCase } from "@/lib/text";
import { Loader2 } from "lucide-react";

// Every Find a Part filter that is a tap (Search S5). The drawer copies these, changes them on its own, and hands
// them over only on "Show results".
export const TAP_KEYS = ["make", "model", "year_min", "year_max", "part_type_id", "grade", "status", "price", "where",
  "main_photo", "verified", "engine", "category_id", "brand"];

const tc = (o) => ({ ...o, label: titleCase(o.label) });

export function PartTapFilters({ values, counts, onPick, pfx = "", stale = false }) {
  if (!counts) {
    return <div data-testid={`${pfx}tap-filters-loading`} className="py-6 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />Counting…</div>;
  }
  const t = (k) => `${pfx}tap-${k}`;
  const spans = yearSpans(counts.year);
  const span = values.year_min && values.year_max ? `${values.year_min}-${values.year_max}` : "";
  return (
    <div className={`space-y-4 transition-opacity ${stale ? "opacity-50" : ""}`} data-testid={`${pfx}tap-filters`} data-stale={stale ? "1" : "0"}>
      {counts.lower_bound && (
        <p data-testid={`${pfx}tap-lower-bound`} className="text-xs text-[#C76714]">A very common word stops counting at 3,000 — each count here is at least that many.</p>
      )}
      <ChipSection label="Make" testid={t("make-section")}>
        <TapChips testid={t("make")} options={(counts.make || []).map(tc)} value={values.make} onPick={(v) => onPick({ make: v, model: "" })} />
      </ChipSection>
      {values.make && (
        <ChipSection label="Model" hint="this make's models" testid={t("model-section")}>
          <TapChips testid={t("model")} options={(counts.model || []).map(tc)} value={values.model} onPick={(v) => onPick({ model: v })} />
        </ChipSection>
      )}
      <ChipSection label="Year range" testid={t("year-section")}>
        <TapChips testid={t("year")} options={spans} value={span}
          onPick={(v) => { const s = spans.find((x) => x.value === v); onPick({ year_min: s?.from || "", year_max: s?.to || "" }); }} />
      </ChipSection>
      <ChipSection label="Part type" testid={t("part-type-section")}>
        <TapChips testid={t("part-type")} options={(counts.part_type || []).map(tc)} value={values.part_type_id} onPick={(v) => onPick({ part_type_id: v })} />
      </ChipSection>
      <ChipSection label="Condition" hint="grade" testid={t("grade-section")}>
        <TapChips testid={t("grade")} options={(counts.grade || []).map((o) => ({ ...o, label: o.label.length <= 2 ? `Grade ${o.label}` : titleCase(o.label) }))}
          value={values.grade} onPick={(v) => onPick({ grade: v })} />
      </ChipSection>
      <ChipSection label="Status" testid={t("status-section")}>
        <TapChips testid={t("status")} options={(counts.status || []).map((o) => ({ ...o, label: statusLabel(o.value) }))} value={values.status} onPick={(v) => onPick({ status: v })} />
      </ChipSection>
      {counts.price_shown && (
        <ChipSection label="Price" testid={t("price-section")}>
          <TapChips testid={t("price")} options={counts.price} value={values.price} onPick={(v) => onPick({ price: v })} max={6} />
        </ChipSection>
      )}
      <WhereChips where={counts.where} value={values.where} onPick={onPick} t={t} />
      <ChipSection label="Main photo" hint="picked by a person" testid={t("main-photo-section")}>
        <TapChips testid={t("main-photo")} options={counts.main_photo} value={values.main_photo} onPick={(v) => onPick({ main_photo: v })} />
      </ChipSection>
      <ChipSection label="Verified" testid={t("verified-section")}>
        <TapChips testid={t("verified")} options={counts.verified} value={values.verified} onPick={(v) => onPick({ verified: v })} />
      </ChipSection>
      <ChipSection label="Source engine" hint="the engine it came off" testid={t("engine-section")}>
        <TapChips testid={t("engine")} options={(counts.engine || []).map((o) => ({ ...o, label: o.label.toUpperCase() }))} value={values.engine} onPick={(v) => onPick({ engine: v })} />
      </ChipSection>
      <ChipSection label="Category" testid={t("category-section")}>
        <TapChips testid={t("category")} options={(counts.category || []).map(tc)} value={values.category_id} onPick={(v) => onPick({ category_id: v })} />
      </ChipSection>
      <ChipSection label="Brand" testid={t("brand-section")}>
        <TapChips testid={t("brand")} options={(counts.brand || []).map(tc)} value={values.brand} onPick={(v) => onPick({ brand: v })} />
      </ChipSection>
    </div>
  );
}

// Where it is: the property, then a zone inside it — each with its count.
function WhereChips({ where, value, onPick, t }) {
  const places = where?.places || [];
  const site = places.find((p) => p.value === value || (p.zones || []).some((z) => z.value === value));
  return (
    <ChipSection label="Where it is" hint="property, then zone" testid={t("where-section")}>
      <TapChips testid={t("where")} options={places} value={site?.value || ""} onPick={(v) => onPick({ where: v })} />
      {site && site.zones.length > 0 && (
        <div className="mt-2 pl-3 border-l-2 border-orange-200">
          <TapChips testid={t("zone")} options={site.zones} value={value} onPick={(v) => onPick({ where: v || site.value })} />
        </div>
      )}
      {(where?.nowhere || 0) > 0 && (
        <p data-testid={t("where-nowhere")} className="mt-1.5 text-xs text-slate-400">{where.nowhere.toLocaleString()} not inside any property yet</p>
      )}
    </ChipSection>
  );
}

// THE TABLET DRAWER (his yes, below 1280 px): taps change only the drawer's copy, its counts are asked for that copy,
// and nothing moves on the page until "Show results".
export function StagedPartFilters({ engine, onDone, pfx = "m-" }) {
  const { f, setF, paramsFor } = engine;
  const [draft, setDraft] = useState(() => Object.fromEntries(TAP_KEYS.map((k) => [k, f[k] || ""])));
  const [got, setGot] = useState(null);   // {draft, data} — the count and WHICH copy of the drawer it was for
  const seq = useRef(0);
  useEffect(() => {
    const my = ++seq.current;
    const t = setTimeout(() => {
      // no box and no hints: counting the drawer's copy is not a search anybody typed, so it is never recorded
      api.get("/search", { params: paramsFor({ ...f, ...draft }, { page_size: 1, counts: 1, box: undefined, hints: undefined }) })
        .then(({ data }) => { if (my === seq.current) setGot({ draft, data }); })
        .catch(() => { if (my === seq.current) setGot((g) => ({ draft, data: g?.data, failed: true })); });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  // a number only when it was counted for exactly what the drawer holds now — never the last pick's, never after a failure
  const fresh = got && got.draft === draft && !got.failed;
  const total = fresh ? got.data?.parts_total : null;
  return (
    <div data-testid={`${pfx}staged-filters`}>
      <PartTapFilters values={draft} counts={got?.data?.filter_counts} onPick={(p) => setDraft((d) => ({ ...d, ...p }))} pfx={pfx} />
      <div className="sticky bottom-0 -mx-5 mt-5 flex gap-2 border-t border-slate-200 bg-white px-5 py-3">
        <Button data-testid={`${pfx}filters-clear-draft`} variant="outline" className="h-12 rounded-lg border-slate-300"
          onClick={() => setDraft(Object.fromEntries(TAP_KEYS.map((k) => [k, ""])))}>Clear all</Button>
        <Button data-testid="mobile-filters-apply" className="flex-1 h-12 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"
          onClick={() => { setF(draft); onDone(); }}>
          {total == null ? "Show results" : `Show ${total.toLocaleString()}${got?.data?.parts_total_is_lower_bound ? "+" : ""} ${total === 1 ? "result" : "results"}`}
        </Button>
      </div>
    </div>
  );
}
